/**
 * Timeline state poller — closes the read-back gap flagged in NEXT.md item 5
 * (2026-08-21): `bridgeSync.ts::readTimelineState` (`GET
 * /timelines/{consumer}/state`) has been exported since 2026-08-16 but
 * nothing ever called it, so a Song paused, resumed, jumped, or stopped by
 * anything other than THIS browser tab (the OPBridge dashboard, a second
 * device, a future consumer) never reached the PWA — `TimelineLauncher.tsx`'s
 * local visual sim just keeps ticking forward on its own musical-clock math,
 * showing a playhead for a Song that may no longer be running server-side
 * at all.
 *
 * 1Hz while a Song is actually playing server-side, 5s idle otherwise -
 * cadence is server-driven here (the snapshot itself says whether anything is
 * active).
 *
 * PER BRIDGE, AND STAMPED (2026-09-30, next-update-plan 1.1). Two changes,
 * both found behind a false MIDI panic that fired on 8 of 8 launches:
 *
 *   - A timeline plays on its HOME bridge (DEC-224), which is often not the
 *     primary, so reading the primary alone could never see it playing. A
 *     launcher now registers the target it follows (`watchTimelineState`) and
 *     every watched target is read. The primary (`''`) is always read too.
 *   - Every snapshot carries `requestedAt`: when its request LEFT, not when
 *     the reply landed. The launcher compared a snapshot up to 5 s old, taken
 *     before its own launch, read "nothing is playing" as "Ableton stopped
 *     externally" and sent CC120/121/123 on all 16 channels to every bridge
 *     ~100 ms after the press. A consumer now ignores any snapshot requested
 *     before OPBridge acknowledged its launch. Request time rather than reply
 *     time, because a reply can land after the acknowledgement and still
 *     describe the server as it was before it.
 *
 * `kickTimelineStatePoll()` reads immediately (after a launch, say) instead
 * of waiting up to 5 s for the idle cadence.
 *
 * Writes into a plain reactive signal; `TimelineLauncher.tsx` reads it and
 * reconciles its own local sim — this module never touches app state
 * directly, same separation `enginesStatePoll.ts` keeps from `SineLfo.tsx`.
 */

import { createSignal } from 'solid-js';
import { readTimelineState } from './bridgeSync.ts';
import type { TimelineStateSnapshot } from '../opbridge/httpClient.ts';

const POLL_INTERVAL_MS = 1_000;      // 1 Hz while a Song is playing server-side
const IDLE_POLL_INTERVAL_MS = 5_000; // 0.2 Hz otherwise — still catches an
                                     //   external start within a few seconds

/** One bridge's Song state, and when it was asked for. */
export interface StampedTimelineState {
  snap: TimelineStateSnapshot;
  /** `performance.now()` when the request left - see the module doc for why
   *  not the reply time. */
  requestedAt: number;
}

/** Keyed by target string (`''` = the primary). Replaced wholesale on every
 *  update, so a read of it is a tracked dependency. */
const [states, setStates] = createSignal<Readonly<Record<string, StampedTimelineState>>>({});

/** The last successful read for `target`, or `null` when nothing has been
 *  read from it yet. A failed read leaves the prior entry untouched: this
 *  poll only ever corrects TOWARD server truth, and a transient network miss
 *  must never itself invent a false "nothing is playing". Consumers compare
 *  `requestedAt` against their own launch before believing an entry. */
export function timelineStateFor(target: string): StampedTimelineState | null {
  return states()[target] ?? null;
}

/** target -> number of launchers following it. */
const watched = new Map<string, number>();

/** Read `target` on every tick until the returned function is called. Also
 *  reads it right away, so a newly shown launcher is not blind for 5 s. */
export function watchTimelineState(target: string): () => void {
  watched.set(target, (watched.get(target) ?? 0) + 1);
  kickTimelineStatePoll();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const n = (watched.get(target) ?? 1) - 1;
    if (n <= 0) watched.delete(target);
    else watched.set(target, n);
  };
}

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = true;
let inFlight = false;
let kickPending = false;

/** Start the poller. Idempotent — calling twice is a no-op. */
export function startTimelineStatePoll(): void {
  if (!stopped) return;
  stopped = false;
  schedule(POLL_INTERVAL_MS);
}

/** Stop the poller. Used on tear-down / tests. */
export function stopTimelineStatePoll(): void {
  stopped = true;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/** Read every watched bridge now rather than at the next scheduled tick. */
export function kickTimelineStatePoll(): void {
  if (stopped) return;
  if (inFlight) { kickPending = true; return; }
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  void tick();
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

async function tick(): Promise<void> {
  if (stopped) return;
  inFlight = true;
  const targets = Array.from(new Set(['', ...watched.keys()]));
  const reads = await Promise.all(targets.map(async (target) => {
    const requestedAt = performance.now();
    const snap = await readTimelineState(target);
    return { target, snap, requestedAt };
  }));
  const next: Record<string, StampedTimelineState> = { ...states() };
  let changed = false;
  for (const r of reads) {
    if (!r.snap) continue;
    next[r.target] = { snap: r.snap, requestedAt: r.requestedAt };
    changed = true;
  }
  if (changed) setStates(next);
  inFlight = false;
  if (kickPending) {
    kickPending = false;
    void tick();
    return;
  }
  const anyPlaying = Object.values(next).some((s) => s.snap.songIsPlaying);
  schedule(anyPlaying ? POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS);
}
